import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { mailingAPI, usersAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Plus, 
  Upload, 
  Loader2, 
  Mail,
  User,
  MapPin,
  FileText,
  Play,
  CheckCircle,
  Trash2,
  UserPlus,
  Download,
  MessageSquare,
  ArrowRight,
  Filter,
  RefreshCw,
  XCircle,
  AlertTriangle,
  Pencil,
  Eye,
  Phone,
  MessageCircle
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const Mailing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [propostas, setPropostas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [estatisticas, setEstatisticas] = useState(null);
  const [vendedores, setVendedores] = useState([]);
  
  // Filtros
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [filtroVendedor, setFiltroVendedor] = useState('todos');
  const [statusTratamento, setStatusTratamento] = useState('');
  const [tratando, setTratando] = useState(false);
  const [observacaoInline, setObservacaoInline] = useState('');
  
  // Modais
  const [modalNovaPropostaAberto, setModalNovaPropostaAberto] = useState(false);
  const [modalAtribuirAberto, setModalAtribuirAberto] = useState(false);
  const [modalObservacaoAberto, setModalObservacaoAberto] = useState(false);
  const [modalImportarAberto, setModalImportarAberto] = useState(false);
  const [leadAtivoEditando, setLeadAtivoEditando] = useState(false);
  const [leadVisualizando, setLeadVisualizando] = useState(false);
  const [propostaSelecionada, setPropostaSelecionada] = useState(null);
  const [filtroTipo, setFiltroTipo] = useState('TODOS');
  
  // Estados do formulário
  const [novaPropostaData, setNovaPropostaData] = useState({
    numero_proposta: '',
    nome: '',
    cpf_cnpj: '',
    telefone: '',
    cidade: '',
    estado: '',
    observacao: ''
  });
  const [vendedorSelecionado, setVendedorSelecionado] = useState('');
  const [observacaoTexto, setObservacaoTexto] = useState('');
  const [tipoMailingImportar, setTipoMailingImportar] = useState('PROPOSTA');
  const [vendedoresSelecionadosImport, setVendedoresSelecionadosImport] = useState([]);
  const [modoDistribuicao, setModoDistribuicao] = useState('todos'); // 'todos' ou 'seletivo'
  const [arquivoImportar, setArquivoImportar] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [importando, setImportando] = useState(false);

  const isAdmin = user?.perfil === 'ADMIN';
  const isBkoMailing = user?.perfil === 'BKO_MAILING';
  const isSupervisor = user?.perfil === 'SUPERVISOR';
  const isMailingManager = isAdmin || isBkoMailing;

  // Remove ".0" de CPF/CNPJ importados do Excel como número decimal
  const limparDoc = (v) => {
    if (!v) return '-';
    const s = String(v);
    return s.endsWith('.0') ? s.slice(0, -2) : s;
  };
  
  // Limpa telefone para links
  const telLink = (t) => (t || '').replace(/\D/g, '');

  useEffect(() => {
    carregarDados();
  }, [filtroStatus, filtroVendedor]);

  const carregarDados = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtroStatus && filtroStatus !== 'todos') params.status = filtroStatus;
      if (filtroVendedor && filtroVendedor !== 'todos') {
        if (filtroVendedor === 'sem_atribuicao') {
          params.vendedor_id = 'null';
        } else {
          params.vendedor_id = filtroVendedor;
        }
      }
      
      const [propostasRes, vendedoresRes] = await Promise.all([
        mailingAPI.listar(params),
        (isMailingManager || isSupervisor) ? usersAPI.listVendedores() : Promise.resolve({ data: [] })
      ]);
      
      setPropostas(propostasRes.data);
      
      if (isMailingManager || isSupervisor) {
        const userList = vendedoresRes.data || [];
        setVendedores(userList.filter(u => u.ativo !== false && ['VENDEDOR', 'SUPERVISOR'].includes(u.perfil)));
        
        // Carregar estatísticas
        try {
          const statsRes = await mailingAPI.estatisticas();
          setEstatisticas(statsRes.data);
        } catch (e) {
          console.error('Erro ao carregar estatísticas:', e);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar mailing:', error);
      toast.error('Erro ao carregar propostas');
    } finally {
      setLoading(false);
    }
  };

  const handleCriarProposta = async () => {
    if (!novaPropostaData.numero_proposta || !novaPropostaData.cidade || !novaPropostaData.estado) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    setSalvando(true);
    try {
      await mailingAPI.criar(novaPropostaData);
      toast.success('Proposta criada com sucesso!');
      setModalNovaPropostaAberto(false);
      setNovaPropostaData({ numero_proposta: '', nome: '', cpf_cnpj: '', telefone: '', cidade: '', estado: '', observacao: '' });
      carregarDados();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar proposta');
    } finally {
      setSalvando(false);
    }
  };

  const handleImportarExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Salvar arquivo e abrir modal para selecionar tipo
    setArquivoImportar(file);
    setModalImportarAberto(true);
  };

  const confirmarImportacao = async () => {
    if (!arquivoImportar) return;

    setImportando(true);
    setModalImportarAberto(false);
    try {
      const formData = new FormData();
      formData.append('arquivo', arquivoImportar);
      
      // Passar IDs dos vendedores selecionados para distribuição seletiva
      const vendedoresIds = modoDistribuicao === 'seletivo' && vendedoresSelecionadosImport.length > 0
        ? vendedoresSelecionadosImport.join(',')
        : null;
      
      const response = await mailingAPI.importar(formData, tipoMailingImportar, vendedoresIds);
      
      // Mostrar resultado com distribuição
      let msg = response.data.message;
      if (response.data.distribuicao && Object.keys(response.data.distribuicao).length > 0) {
        const dist = Object.entries(response.data.distribuicao)
          .map(([nome, qtd]) => `${nome}: ${qtd}`)
          .join(', ');
        toast.success(`${msg}\n\n📊 Distribuição (${tipoMailingImportar}): ${dist}`, { duration: 6000 });
      } else {
        toast.success(msg);
      }
      
      if (response.data.erros?.length > 0) {
        toast.warning(`${response.data.erros.length} erros durante importação`);
      }
      
      carregarDados();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao importar arquivo');
    } finally {
      setImportando(false);
      setArquivoImportar(null);
      setTipoMailingImportar('PROPOSTA');
      setVendedoresSelecionadosImport([]);
      setModoDistribuicao('todos');
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleAtribuir = async () => {
    if (!vendedorSelecionado) {
      toast.error('Selecione um vendedor');
      return;
    }

    setSalvando(true);
    try {
      const res = await mailingAPI.atribuir(propostaSelecionada.id, vendedorSelecionado);
      toast.success(res.data?.message || 'Proposta atribuída com sucesso!');
      setModalAtribuirAberto(false);
      setVendedorSelecionado('');
      carregarDados();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atribuir proposta');
    } finally {
      setSalvando(false);
    }
  };

  const handleTrabalhar = async (proposta) => {
    try {
      await mailingAPI.trabalhar(proposta.id);
      toast.success('Proposta em tratamento!');
      carregarDados();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao iniciar trabalho');
    }
  };

  const handleSalvarObservacao = async () => {
    setSalvando(true);
    try {
      await mailingAPI.atualizarObservacao(propostaSelecionada.id, observacaoTexto);
      toast.success('Observação salva!');
      setModalObservacaoAberto(false);
      carregarDados();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar observação');
    } finally {
      setSalvando(false);
    }
  };

  const handleFecharVenda = async (proposta) => {
    if (!window.confirm('Deseja fechar esta venda e ir para Nova Venda?')) return;

    try {
      const response = await mailingAPI.fecharVenda(proposta.id);
      toast.success('Venda fechada! Redirecionando...');
      
      // Navegar para Nova Venda com dados preenchidos
      navigate('/vendas/nova', { 
        state: { 
          dadosMailing: response.data.dados_nova_venda 
        } 
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao fechar venda');
    }
  };

  const handleExcluir = async (proposta) => {
    if (!window.confirm(`Excluir proposta ${proposta.numero_proposta}?`)) return;

    try {
      await mailingAPI.excluir(proposta.id);
      toast.success('Proposta excluída!');
      carregarDados();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir');
    }
  };

  const handleExcluirTodos = async () => {
    if (!window.confirm('⚠️ ATENÇÃO: Deseja excluir TODAS as propostas do mailing? Esta ação não pode ser desfeita!')) return;
    if (!window.confirm('Tem certeza ABSOLUTA? Todas as propostas serão perdidas!')) return;

    try {
      const response = await mailingAPI.excluirTodos();
      toast.success(response.data.message);
      carregarDados();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir');
    }
  };

  const handleExportar = async () => {
    try {
      toast.info('Gerando exportação...');
      const response = await mailingAPI.exportar();
      
      // Criar blob e baixar
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `mailing_export_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Exportação concluída!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao exportar');
    }
  };

  const handleNegar = async (proposta) => {
    if (!window.confirm(`Marcar proposta ${proposta.numero_proposta} como NEGADA?`)) return;

    try {
      await mailingAPI.negar(proposta.id);
      toast.success('Proposta marcada como negada!');
      carregarDados();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao negar proposta');
    }
  };

  const handleTratarLead = async (propostaId) => {
    if (!statusTratamento) {
      toast.error('Selecione um status para tratar o lead');
      return;
    }

    // Salvar observação se preenchida
    if (observacaoInline.trim() && leadAtivo) {
      try {
        await mailingAPI.atualizarObservacao(propostaId, observacaoInline.trim());
      } catch (e) {}
    }

    setTratando(true);
    try {
      const response = await mailingAPI.tratar(propostaId, statusTratamento);
      toast.success(response.data.message);

      // Se VENDA REALIZADA, redirecionar para Nova Venda com dados do lead
      if (statusTratamento === 'VENDA REALIZADA' && leadAtivo) {
        navigate('/vendas/nova', {
          state: {
            dadosMailing: {
              numero_proposta: leadAtivo.numero_proposta,
              cidade: leadAtivo.cidade,
              estado: leadAtivo.estado,
              observacao: observacaoInline.trim() || leadAtivo.observacao,
            }
          }
        });
        return;
      }

      if (response.data.tem_proximo) {
        toast.info(`Proximo lead: ${response.data.proximo_lead}`, { duration: 3000 });
      } else {
        toast.info('Nenhum lead pendente. Aguarde novas distribuicoes.', { duration: 3000 });
      }
      setStatusTratamento('');
      setObservacaoInline('');
      carregarDados();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao tratar lead');
    } finally {
      setTratando(false);
    }
  };

  const leadAtivo = propostas.find(p => p._is_lead_ativo);
  const leadsTratados = propostas.filter(p => !p._is_lead_ativo && p.status !== 'Novo');

  // Carregar observação do lead ativo
  useEffect(() => {
    if (leadAtivo) {
      setObservacaoInline(leadAtivo.observacao || '');
    }
  }, [leadAtivo?.id]);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Novo':
        return <Badge className="bg-red-100 text-red-800">Novo</Badge>;
      case 'Em Tratamento':
        return <Badge className="bg-yellow-100 text-yellow-800">Em Tratamento</Badge>;
      case 'Fechado':
        return <Badge className="bg-green-100 text-green-800">Fechado</Badge>;
      case 'Negado':
        return <Badge className="bg-red-100 text-red-800">Negado</Badge>;
      case 'ATENDIDO':
        return <Badge className="bg-red-100 text-red-800">Atendido</Badge>;
      case 'SEM INTERESSE':
        return <Badge className="bg-orange-100 text-orange-800">Sem Interesse</Badge>;
      case 'VENDA REALIZADA':
        return <Badge className="bg-gray-100 text-gray-800">Venda Realizada</Badge>;
      case 'NAO ATENDE':
        return <Badge className="bg-gray-100 text-gray-800">Nao Atende</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const estados = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
    'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
    'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Mail className="h-7 w-7 text-red-600" />
            Mailing
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            {(isMailingManager || isSupervisor) ? 'Gerencie propostas e distribua para vendedores' : 'Suas propostas para trabalhar'}
          </p>
        </div>
        
        {(isMailingManager || isSupervisor) && (
          <div className="flex gap-2">
            {isMailingManager && (
              <Button 
                variant="outline" 
                onClick={handleExcluirTodos}
                className="text-red-600 hover:bg-red-50"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Excluir Todos
              </Button>
            )}
            <Button 
              variant="outline" 
              onClick={handleExportar}
              className="text-green-600 hover:bg-green-50"
            >
              <Download className="mr-2 h-4 w-4" />
              Exportar Excel
            </Button>
            {isMailingManager && (
              <Button 
                variant="outline" 
                onClick={() => setModalImportarAberto(true)}
                disabled={importando}
              >
                {importando ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="mr-2 h-4 w-4" />
                )}
                Importar Excel
              </Button>
            )}
            {isMailingManager && (
              <Button 
                className="bg-red-600 hover:bg-red-700"
                onClick={() => setModalNovaPropostaAberto(true)}
              >
                <Plus className="mr-2 h-4 w-4" />
                Nova Proposta
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Estatísticas (ADMIN e SUPERVISOR) */}
      {(isMailingManager || isSupervisor) && estatisticas && (
        <div className="grid grid-cols-6 gap-4">
          <Card className="bg-gray-50">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-gray-900">{estatisticas.total}</div>
              <div className="text-sm text-gray-500">Total</div>
            </CardContent>
          </Card>
          <Card className="bg-red-50">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-red-600">{estatisticas.novo}</div>
              <div className="text-sm text-red-600">Novas</div>
            </CardContent>
          </Card>
          <Card className="bg-yellow-50">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-yellow-600">{estatisticas.em_tratamento}</div>
              <div className="text-sm text-yellow-600">Em Tratamento</div>
            </CardContent>
          </Card>
          <Card className="bg-green-50">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-green-600">{estatisticas.fechado}</div>
              <div className="text-sm text-green-600">Fechadas</div>
            </CardContent>
          </Card>
          <Card className="bg-red-50">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-red-600">{estatisticas.negado || 0}</div>
              <div className="text-sm text-red-600">Negadas</div>
            </CardContent>
          </Card>
          <Card className="bg-orange-50">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-orange-600">{estatisticas.sem_atribuicao}</div>
              <div className="text-sm text-orange-600">Sem Atribuição</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filtros (ADMIN e SUPERVISOR) */}
      {(isMailingManager || isSupervisor) && (
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center gap-4">
              <Filter className="h-5 w-5 text-gray-400" />
              <div className="flex-1 grid grid-cols-3 gap-4">
                <Select value={filtroStatus} onValueChange={setFiltroStatus}>
                  <SelectTrigger>
                    <SelectValue placeholder="Filtrar por Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos os Status</SelectItem>
                    <SelectItem value="Novo">Novo</SelectItem>
                    <SelectItem value="Em Tratamento">Em Tratamento</SelectItem>
                    <SelectItem value="Fechado">Fechado</SelectItem>
                    <SelectItem value="Negado">Negado</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select value={filtroVendedor} onValueChange={setFiltroVendedor}>
                  <SelectTrigger>
                    <SelectValue placeholder="Filtrar por Vendedor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos os Vendedores</SelectItem>
                    <SelectItem value="sem_atribuicao">Sem Atribuição</SelectItem>
                    {vendedores.map(v => (
                      <SelectItem key={v.id} value={v.id}>{v.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Button variant="outline" onClick={carregarDados}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Atualizar
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lead Ativo (VENDEDOR apenas, não Supervisor) */}
      {!isMailingManager && !isSupervisor && (
        <div className="space-y-4" data-testid="mailing-vendedor-view">
          {leadAtivo ? (
            <Card className="border-2 border-red-400 bg-red-50/30">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-red-800">
                  <AlertTriangle className="h-5 w-5" />
                  Lead Ativo - Trate para liberar o proximo
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                  {/* Campos TT */}
                  {(!leadAtivo.tipo_mailing || leadAtivo.tipo_mailing === 'TT') && (
                    <>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Cidade</p>
                        <p className="font-bold text-lg">{leadAtivo.cidade || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Contrato Antigo</p>
                        <p className="font-semibold">{leadAtivo.contrato_antigo || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Cliente Desde</p>
                        <p className="font-semibold">{leadAtivo.cliente_desde || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Nome Cliente Antigo</p>
                        <p className="font-semibold">{leadAtivo.nome_antigo || leadAtivo.nome || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Perfil do Cliente</p>
                        <p className="font-semibold">{leadAtivo.perfil_cliente || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Dados do Novo</p>
                        <p className="font-semibold">{leadAtivo.dados_novo || '-'}</p>
                      </div>
                    </>
                  )}
                  {/* Campos PROSPECT */}
                  {leadAtivo.tipo_mailing === 'PROPOSTA' && (
                    <>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Nome Cliente</p>
                        <p className="font-bold text-lg">{leadAtivo.nome || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">CPF/CNPJ</p>
                        <p className="font-semibold">{limparDoc(leadAtivo.cpf_cnpj)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Nascimento</p>
                        <p className="font-semibold">{leadAtivo.nascimento || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Nome da Mae</p>
                        <p className="font-semibold">{leadAtivo.nome_mae || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Telefone</p>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold">{leadAtivo.telefone || '-'}</p>
                          {leadAtivo.telefone && isMailingManager && (
                            <div className="flex items-center gap-1">
                              <a href={`tel:${telLink(leadAtivo.telefone)}`} title="Ligar" className="p-1 rounded hover:bg-green-100 text-green-600"><Phone className="w-4 h-4" /></a>
                              <a href={`https://wa.me/55${telLink(leadAtivo.telefone)}`} target="_blank" rel="noreferrer" title="WhatsApp" className="p-1 rounded hover:bg-emerald-100 text-emerald-600"><MessageCircle className="w-4 h-4" /></a>
                            </div>
                          )}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">E-mail</p>
                        <p className="font-semibold">{leadAtivo.email_lead || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Endereco</p>
                        <p className="font-semibold">{leadAtivo.endereco || '-'}, {leadAtivo.numero_end || ''}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Bairro</p>
                        <p className="font-semibold">{leadAtivo.bairro || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Cidade/UF</p>
                        <p className="font-semibold">{leadAtivo.cidade || '-'}/{leadAtivo.estado || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">CEP</p>
                        <p className="font-semibold">{leadAtivo.cep || '-'}</p>
                      </div>
                      {leadAtivo.complemento && (
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Complemento</p>
                          <p className="font-semibold">{leadAtivo.complemento}</p>
                        </div>
                      )}
                    </>
                  )}
                  {/* Campos BASE */}
                  {leadAtivo.tipo_mailing === 'BASE' && (
                    <>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Nome</p>
                        <p className="font-bold text-lg">{leadAtivo.nome || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Contrato</p>
                        <p className="font-semibold">{leadAtivo.contrato || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">HP</p>
                        <p className="font-semibold">{leadAtivo.hp || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Cidade</p>
                        <p className="font-semibold">{leadAtivo.cidade || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Telefone</p>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold">{leadAtivo.telefone || '-'}</p>
                          {leadAtivo.telefone && isMailingManager && (
                            <div className="flex items-center gap-1">
                              <a href={`tel:${telLink(leadAtivo.telefone)}`} title="Ligar" className="p-1 rounded hover:bg-green-100 text-green-600"><Phone className="w-4 h-4" /></a>
                              <a href={`https://wa.me/55${telLink(leadAtivo.telefone)}`} target="_blank" rel="noreferrer" title="WhatsApp" className="p-1 rounded hover:bg-emerald-100 text-emerald-600"><MessageCircle className="w-4 h-4" /></a>
                            </div>
                          )}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Endereco</p>
                        <p className="font-semibold">{leadAtivo.endereco || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">CPF/CNPJ</p>
                        <p className="font-semibold">{limparDoc(leadAtivo.cpf_cnpj)}</p>
                      </div>
                    </>
                  )}
                  {leadAtivo.telefone && (!leadAtivo.tipo_mailing || leadAtivo.tipo_mailing === 'TT') && (
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Telefone</p>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold">{leadAtivo.telefone}</p>
                        {isMailingManager && (
                          <div className="flex items-center gap-1">
                            <a href={`tel:${telLink(leadAtivo.telefone)}`} title="Ligar" className="p-1 rounded hover:bg-green-100 text-green-600"><Phone className="w-4 h-4" /></a>
                            <a href={`https://wa.me/55${telLink(leadAtivo.telefone)}`} target="_blank" rel="noreferrer" title="WhatsApp" className="p-1 rounded hover:bg-emerald-100 text-emerald-600"><MessageCircle className="w-4 h-4" /></a>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {leadAtivo.cpf_cnpj && (!leadAtivo.tipo_mailing || leadAtivo.tipo_mailing === 'TT') && (
                    <div>
                      <p className="text-xs text-gray-500 uppercase">CPF/CNPJ</p>
                      <p className="font-semibold">{limparDoc(leadAtivo.cpf_cnpj)}</p>
                    </div>
                  )}
                </div>

                {/* Observação inline editável */}
                <div className="mb-4">
                  <Label className="text-xs text-gray-500 uppercase">Observacao</Label>
                  <Textarea
                    value={observacaoInline}
                    onChange={(e) => setObservacaoInline(e.target.value)}
                    placeholder="Escreva sua observacao sobre o lead..."
                    className="mt-1"
                    rows={2}
                    data-testid="observacao-inline"
                  />
                </div>

                <div className="flex items-center gap-3 pt-3 border-t">
                  <Select value={statusTratamento} onValueChange={setStatusTratamento}>
                    <SelectTrigger className="w-56" data-testid="status-tratamento-select">
                      <SelectValue placeholder="Selecione o status..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="EM TRATATIVA">Em Tratativa</SelectItem>
                      <SelectItem value="AGUARDANDO CLIENTE">Aguardando Cliente</SelectItem>
                      <SelectItem value="FINALIZADO">Finalizado</SelectItem>
                      <SelectItem value="CANCELADO">Cancelado</SelectItem>
                      <SelectItem value="VENDA REALIZADA">Venda Realizada</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={() => handleTratarLead(leadAtivo.id)}
                    disabled={!statusTratamento || tratando}
                    className={statusTratamento === 'VENDA REALIZADA' ? 'bg-gray-700 hover:bg-gray-800' : 'bg-red-600 hover:bg-red-700'}
                    data-testid="tratar-lead-btn"
                  >
                    {tratando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 
                     statusTratamento === 'VENDA REALIZADA' ? <ArrowRight className="mr-2 h-4 w-4" /> : <CheckCircle className="mr-2 h-4 w-4" />}
                    {statusTratamento === 'VENDA REALIZADA' ? 'Ir para Nova Venda' : 'Tratar Lead'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-dashed border-2 border-gray-300">
              <CardContent className="py-12 text-center">
                <Mail className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                <h3 className="text-lg font-semibold text-gray-600">Nenhum lead pendente</h3>
                <p className="text-sm text-gray-500 mt-1">Aguarde novas distribuicoes do Admin</p>
              </CardContent>
            </Card>
          )}

          {/* Leads já tratados */}
          {leadsTratados.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Leads Tratados ({leadsTratados.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left p-2">Cidade</th>
                        <th className="text-left p-2">Contrato Antigo</th>
                        <th className="text-left p-2">Nome Antigo</th>
                        <th className="text-left p-2">Perfil</th>
                        <th className="text-left p-2">Dados do Novo</th>
                        <th className="text-left p-2">Status</th>
                        <th className="text-left p-2">Observacao</th>
                        <th className="text-left p-2">Acoes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leadsTratados.map(lead => (
                        <tr key={lead.id} className="border-b">
                          <td className="p-2 font-medium">{lead.cidade || '-'}</td>
                          <td className="p-2">{lead.contrato_antigo || '-'}</td>
                          <td className="p-2">{lead.nome_antigo || lead.nome || '-'}</td>
                          <td className="p-2">{lead.perfil_cliente || '-'}</td>
                          <td className="p-2">{lead.dados_novo || '-'}</td>
                          <td className="p-2">{getStatusBadge(lead.status)}</td>
                          <td className="p-2 text-gray-600 text-xs">{lead.observacao_atendimento || lead.observacao || '-'}</td>
                          <td className="p-2">
                            {(lead.status === 'EM TRATATIVA' || lead.status === 'AGUARDANDO CLIENTE') && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setPropostaSelecionada(lead);
                                  setStatusTratamento(lead.status);
                                  setLeadAtivoEditando(true);
                                }}
                              >
                                <Pencil className="h-4 w-4 text-red-600" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Lista de Propostas (ADMIN) */}
      {(isMailingManager || isSupervisor) && (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Propostas ({propostas.length})</span>
            <div className="flex gap-2">
              <Button size="sm" variant={filtroTipo === 'TODOS' ? 'default' : 'outline'} onClick={() => setFiltroTipo('TODOS')}>Todos</Button>
              <Button size="sm" variant={filtroTipo === 'TT' ? 'default' : 'outline'} onClick={() => setFiltroTipo('TT')}>TT</Button>
              <Button size="sm" variant={filtroTipo === 'PROPOSTA' ? 'default' : 'outline'} onClick={() => setFiltroTipo('PROPOSTA')}>Prospect</Button>
              <Button size="sm" variant={filtroTipo === 'BASE' ? 'default' : 'outline'} onClick={() => setFiltroTipo('BASE')}>Base</Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-red-600" />
              <p className="text-gray-500 mt-2">Carregando propostas...</p>
            </div>
          ) : propostas.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <Mail className="h-12 w-12 mx-auto text-gray-400 mb-3" />
              <p className="text-gray-500">
                {isMailingManager ? 'Nenhuma proposta cadastrada' : 'Nenhuma proposta atribuída a você'}
              </p>
              {isMailingManager && (
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => setModalNovaPropostaAberto(true)}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Criar primeira proposta
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              {(() => {
                const propostasFiltradas = filtroTipo === 'TODOS' ? propostas : propostas.filter(p => p.tipo_mailing === filtroTipo);
                const temProspect = filtroTipo === 'PROPOSTA' || (filtroTipo === 'TODOS' && propostasFiltradas.some(p => p.tipo_mailing === 'PROPOSTA'));
                const temTT = filtroTipo === 'TT' || (filtroTipo === 'TODOS' && propostasFiltradas.some(p => p.tipo_mailing !== 'PROPOSTA'));
                const temBase = filtroTipo === 'BASE' || (filtroTipo === 'TODOS' && propostasFiltradas.some(p => p.tipo_mailing === 'BASE'));
                return (
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-3 font-medium text-gray-600">Nome</th>
                    <th className="text-left p-3 font-medium text-gray-600">CPF/CNPJ</th>
                    <th className="text-left p-3 font-medium text-gray-600">Telefone</th>
                    <th className="text-left p-3 font-medium text-gray-600">Cidade</th>
                    {filtroTipo !== 'PROPOSTA' && <th className="text-left p-3 font-medium text-gray-600">Contrato</th>}
                    {filtroTipo === 'BASE' && <th className="text-left p-3 font-medium text-gray-600">HP</th>}
                    {filtroTipo === 'BASE' && <th className="text-left p-3 font-medium text-gray-600">Endereço</th>}
                    {filtroTipo === 'PROPOSTA' && <th className="text-left p-3 font-medium text-gray-600">E-mail</th>}
                    {filtroTipo === 'PROPOSTA' && <th className="text-left p-3 font-medium text-gray-600">Endereco</th>}
                    {filtroTipo === 'PROPOSTA' && <th className="text-left p-3 font-medium text-gray-600">Bairro</th>}
                    {(isMailingManager || isSupervisor) && <th className="text-left p-3 font-medium text-gray-600">Vendedor</th>}
                    <th className="text-left p-3 font-medium text-gray-600">Status</th>
                    {(isMailingManager || isSupervisor) && <th className="text-left p-3 font-medium text-gray-600">Tratado por</th>}
                    <th className="text-right p-3 font-medium text-gray-600">Acoes</th>
                  </tr>
                </thead>
                <tbody>
                  {propostasFiltradas.map((proposta) => (
                    <tr key={proposta.id} className="border-b hover:bg-gray-50">
                      <td className="p-3 font-medium text-gray-900">{proposta.nome || proposta.nome_antigo || '-'}</td>
                      <td className="p-3 text-gray-700 text-sm">{limparDoc(proposta.cpf_cnpj)}</td>
                      <td className="p-3 text-gray-700 text-sm">
                        <div className="flex items-center gap-2">
                          <span>{limparDoc(proposta.telefone)}</span>
                          {proposta.telefone && isMailingManager && (
                            <div className="flex items-center gap-1">
                              <a href={`tel:${telLink(proposta.telefone)}`} title="Ligar" className="p-1 rounded hover:bg-green-100 text-green-600"><Phone className="w-3.5 h-3.5" /></a>
                              <a href={`https://wa.me/55${telLink(proposta.telefone)}`} target="_blank" rel="noreferrer" title="WhatsApp" className="p-1 rounded hover:bg-emerald-100 text-emerald-600"><MessageCircle className="w-3.5 h-3.5" /></a>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="p-3 text-gray-700 text-sm">{proposta.cidade || '-'}</td>
                      {filtroTipo !== 'PROPOSTA' && <td className="p-3 text-gray-700 text-sm">{proposta.contrato_antigo || proposta.contrato || '-'}</td>}
                      {filtroTipo === 'BASE' && <td className="p-3 text-gray-700 text-sm">{proposta.hp || '-'}</td>}
                      {filtroTipo === 'BASE' && <td className="p-3 text-gray-700 text-sm">{proposta.endereco || '-'}</td>}
                      {filtroTipo === 'PROPOSTA' && <td className="p-3 text-gray-700 text-sm">{proposta.email_lead || '-'}</td>}
                      {filtroTipo === 'PROPOSTA' && <td className="p-3 text-gray-700 text-sm">{proposta.endereco ? `${proposta.endereco}, ${proposta.numero_end || ''}` : '-'}</td>}
                      {filtroTipo === 'PROPOSTA' && <td className="p-3 text-gray-700 text-sm">{proposta.bairro || '-'}</td>}
                      {(isMailingManager || isSupervisor) && (
                        <td className="p-3">
                          {proposta.vendedor_nome ? (
                            <span className="flex items-center gap-1 text-gray-700">
                              <User className="h-4 w-4" />
                              {proposta.vendedor_nome}
                            </span>
                          ) : (
                            <span className="text-orange-600 text-sm">Sem atribuição</span>
                          )}
                        </td>
                      )}
                      <td className="p-3">{getStatusBadge(proposta.status)}</td>
                      {(isMailingManager || isSupervisor) && (
                        <td className="p-3 text-sm text-gray-700">{proposta.vendedor_tratou || '-'}</td>
                      )}
                      <td className="p-3">
                        <div className="flex items-center justify-end gap-1">
                          {/* Visualizar detalhes */}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setPropostaSelecionada(proposta);
                              setLeadVisualizando(true);
                            }}
                            title="Ver detalhes"
                            data-testid={`ver-lead-${proposta.id}`}
                          >
                            <Eye className="h-4 w-4 text-gray-600" />
                          </Button>
                          
                          {/* Atribuir/Transferir (ADMIN/BKO_MAILING; SUPERVISOR só dentro da equipe) */}
                          {(isMailingManager || isSupervisor) && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setPropostaSelecionada(proposta);
                                setVendedorSelecionado(proposta.vendedor_id || '');
                                setModalAtribuirAberto(true);
                              }}
                              title={isSupervisor ? 'Transferir para outro vendedor da equipe' : 'Atribuir vendedor'}
                              data-testid={`atribuir-lead-${proposta.id}`}
                            >
                              <UserPlus className="h-4 w-4 text-red-600" />
                            </Button>
                          )}
                          
                          {/* Trabalhar (se status for Novo) */}
                          {proposta.status === 'Novo' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleTrabalhar(proposta)}
                              title="Iniciar trabalho"
                            >
                              <Play className="h-4 w-4 text-yellow-600" />
                            </Button>
                          )}
                          
                          {/* Editar - status intermediários */}
                          {(proposta.status === 'EM TRATATIVA' || proposta.status === 'AGUARDANDO CLIENTE') && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setPropostaSelecionada(proposta);
                                setStatusTratamento(proposta.status);
                                setLeadAtivoEditando(true);
                              }}
                              title="Editar lead"
                              data-testid={`editar-lead-${proposta.id}`}
                            >
                              <Pencil className="h-4 w-4 text-red-600" />
                            </Button>
                          )}
                          
                          {/* Observação */}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setPropostaSelecionada(proposta);
                              setObservacaoTexto(proposta.observacao || '');
                              setModalObservacaoAberto(true);
                            }}
                            title="Editar observação"
                          >
                            <MessageSquare className="h-4 w-4 text-gray-600" />
                          </Button>
                          
                          {/* Fechar Venda (se em tratamento) */}
                          {proposta.status === 'Em Tratamento' && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleFecharVenda(proposta)}
                                title="Fechar venda"
                                className="text-green-600 hover:text-green-700"
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleNegar(proposta)}
                                title="Negar proposta"
                                className="text-red-600 hover:text-red-700"
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          
                          {/* Excluir (ADMIN/BKO_MAILING) */}
                          {isMailingManager && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleExcluir(proposta)}
                              title="Excluir"
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
                );
              })()}
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Modal Nova Proposta */}
      <Dialog open={modalNovaPropostaAberto} onOpenChange={setModalNovaPropostaAberto}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-red-600" />
              Nova Proposta
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nome do Cliente</Label>
                <Input
                  value={novaPropostaData.nome}
                  onChange={(e) => setNovaPropostaData({ ...novaPropostaData, nome: e.target.value.toUpperCase() })}
                  placeholder="Ex: Joao Silva"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>CPF/CNPJ</Label>
                <Input
                  value={novaPropostaData.cpf_cnpj}
                  onChange={(e) => setNovaPropostaData({ ...novaPropostaData, cpf_cnpj: e.target.value })}
                  placeholder="Ex: 123.456.789-00"
                  className="mt-1"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Numero da Proposta <span className="text-red-500">*</span></Label>
                <Input
                  value={novaPropostaData.numero_proposta}
                  onChange={(e) => setNovaPropostaData({ ...novaPropostaData, numero_proposta: e.target.value.toUpperCase() })}
                  placeholder="Ex: P-000123"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Telefone</Label>
                <Input
                  value={novaPropostaData.telefone}
                  onChange={(e) => setNovaPropostaData({ ...novaPropostaData, telefone: e.target.value })}
                  placeholder="Ex: (11) 99999-9999"
                  className="mt-1"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Cidade <span className="text-red-500">*</span></Label>
                <Input
                  value={novaPropostaData.cidade}
                  onChange={(e) => setNovaPropostaData({ ...novaPropostaData, cidade: e.target.value.toUpperCase() })}
                  placeholder="Ex: Sao Paulo"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Estado <span className="text-red-500">*</span></Label>
                <Select 
                  value={novaPropostaData.estado} 
                  onValueChange={(v) => setNovaPropostaData({ ...novaPropostaData, estado: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Selecione o estado" />
                  </SelectTrigger>
                  <SelectContent>
                    {estados.map(uf => (
                      <SelectItem key={uf} value={uf}>{uf}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Observacao (opcional)</Label>
              <Textarea
                value={novaPropostaData.observacao}
                onChange={(e) => setNovaPropostaData({ ...novaPropostaData, observacao: e.target.value })}
                placeholder="Informacoes adicionais..."
                className="mt-1"
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalNovaPropostaAberto(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleCriarProposta} 
              disabled={salvando}
              className="bg-red-600 hover:bg-red-700"
            >
              {salvando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
              Criar Proposta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Atribuir Vendedor */}
      <Dialog open={modalAtribuirAberto} onOpenChange={setModalAtribuirAberto}>
        <DialogContent data-testid="modal-atribuir-vendedor">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-red-600" />
              {isSupervisor ? 'Transferir Vendedor' : 'Atribuir Vendedor'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-gray-600 mb-1">
              Proposta: <strong>{propostaSelecionada?.numero_proposta || propostaSelecionada?.nome || propostaSelecionada?.nome_antigo || '-'}</strong>
            </p>
            {propostaSelecionada?.vendedor_nome && (
              <p className="text-sm text-gray-600 mb-4" data-testid="atribuir-vendedor-atual">
                Vendedor atual: <strong>{propostaSelecionada.vendedor_nome}</strong>
              </p>
            )}
            
            <Label>{isSupervisor ? 'Transferir para' : 'Selecione o Vendedor'}</Label>
            <Select value={vendedorSelecionado} onValueChange={setVendedorSelecionado}>
              <SelectTrigger className="mt-1" data-testid="select-vendedor-atribuir">
                <SelectValue placeholder="Selecione..." />
              </SelectTrigger>
              <SelectContent>
                {vendedores.map(v => (
                  <SelectItem key={v.id} value={v.id} data-testid={`opcao-vendedor-${v.id}`}>
                    {v.nome} ({v.perfil})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isSupervisor && (
              <p className="text-xs text-gray-500 mt-2">Apenas vendedores da sua equipe estão disponíveis.</p>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalAtribuirAberto(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleAtribuir} 
              disabled={salvando || !vendedorSelecionado || vendedorSelecionado === propostaSelecionada?.vendedor_id}
              className="bg-red-600 hover:bg-red-700"
              data-testid="btn-confirmar-atribuir"
            >
              {salvando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserPlus className="mr-2 h-4 w-4" />}
              {isSupervisor ? 'Transferir' : 'Atribuir'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Observação */}
      <Dialog open={modalObservacaoAberto} onOpenChange={setModalObservacaoAberto}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-gray-600" />
              Observação
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-gray-600 mb-4">
              Proposta: <strong>{propostaSelecionada?.numero_proposta}</strong>
            </p>
            
            <Label>Observação</Label>
            <Textarea
              value={observacaoTexto}
              onChange={(e) => setObservacaoTexto(e.target.value)}
              placeholder="Digite a observação..."
              className="mt-1"
              rows={4}
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalObservacaoAberto(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSalvarObservacao} 
              disabled={salvando}
              className="bg-red-600 hover:bg-red-700"
            >
              {salvando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle className="mr-2 h-4 w-4" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Importar - Selecionar Tipo */}
      <Dialog open={modalImportarAberto} onOpenChange={setModalImportarAberto}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-red-600" />
              Importar Mailing
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div>
              <Label>Tipo de Mailing <span className="text-red-500">*</span></Label>
              <Select value={tipoMailingImportar} onValueChange={setTipoMailingImportar}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="TT">TT</SelectItem>
                  <SelectItem value="PROPOSTA">PROSPECT</SelectItem>
                  <SelectItem value="BASE">BASE</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-sm text-gray-800 font-medium mb-2">Formato da planilha ({tipoMailingImportar}):</p>
              {tipoMailingImportar === 'TT' ? (
                <div className="grid grid-cols-2 gap-1 text-xs">
                  <span className="bg-gray-100 px-2 py-1 rounded font-semibold">CIDADE</span>
                  <span className="bg-gray-100 px-2 py-1 rounded font-semibold">CONTRATO ANTIGO</span>
                  <span className="bg-gray-100 px-2 py-1 rounded font-semibold">CLIENTE DESDE</span>
                  <span className="bg-gray-100 px-2 py-1 rounded font-semibold">NOME DO CLIENTE ANTIGO</span>
                  <span className="bg-gray-100 px-2 py-1 rounded font-semibold">PERFIL DO CLIENTE</span>
                  <span className="bg-gray-100 px-2 py-1 rounded font-semibold">DADOS DO NOVO</span>
                  <span className="bg-gray-100 px-2 py-1 rounded font-semibold col-span-2">OBSERVAÇÃO</span>
                </div>
              ) : tipoMailingImportar === 'BASE' ? (
                <div className="grid grid-cols-2 gap-1 text-xs">
                  <span className="bg-orange-100 px-2 py-1 rounded font-semibold">CIDADE</span>
                  <span className="bg-orange-100 px-2 py-1 rounded font-semibold">CONTRATO</span>
                  <span className="bg-orange-100 px-2 py-1 rounded font-semibold">HP</span>
                  <span className="bg-orange-100 px-2 py-1 rounded font-semibold">NOME</span>
                  <span className="bg-orange-100 px-2 py-1 rounded font-semibold">TELEFONE</span>
                  <span className="bg-orange-100 px-2 py-1 rounded font-semibold">ENDEREÇO</span>
                  <span className="bg-orange-100 px-2 py-1 rounded font-semibold col-span-2">CPF/CNPJ</span>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-1 text-xs">
                  <span className="bg-green-100 px-2 py-1 rounded font-semibold">NOME CLIENTE</span>
                  <span className="bg-green-100 px-2 py-1 rounded font-semibold">CPF/CNPJ</span>
                  <span className="bg-green-100 px-2 py-1 rounded font-semibold">NASCIMENTO</span>
                  <span className="bg-green-100 px-2 py-1 rounded font-semibold">NOME DA MAE</span>
                  <span className="bg-green-100 px-2 py-1 rounded font-semibold">TELEFONE</span>
                  <span className="bg-green-100 px-2 py-1 rounded font-semibold">E-MAIL</span>
                  <span className="bg-green-100 px-2 py-1 rounded font-semibold">ENDEREÇO</span>
                  <span className="bg-green-100 px-2 py-1 rounded font-semibold">NUMERO</span>
                  <span className="bg-green-100 px-2 py-1 rounded font-semibold">COMPLEMENTO</span>
                  <span className="bg-green-100 px-2 py-1 rounded font-semibold">BAIRRO</span>
                  <span className="bg-green-100 px-2 py-1 rounded font-semibold">CIDADE</span>
                  <span className="bg-green-100 px-2 py-1 rounded font-semibold">UF</span>
                  <span className="bg-green-100 px-2 py-1 rounded font-semibold col-span-2">CEP</span>
                </div>
              )}
              <p className="text-xs text-red-600 mt-2">A primeira linha deve conter os nomes das colunas exatamente como acima.</p>
            </div>

            {/* Modo de Distribuição */}
            <div className="space-y-3">
              <Label className="font-medium">Distribuir para</Label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => { setModoDistribuicao('todos'); setVendedoresSelecionadosImport([]); }}
                  className={`flex-1 p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                    modoDistribuicao === 'todos' 
                      ? 'border-red-500 bg-red-50 text-red-700' 
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                  data-testid="dist-todos-btn"
                >
                  Todos Vendedores
                </button>
                <button
                  type="button"
                  onClick={() => setModoDistribuicao('seletivo')}
                  className={`flex-1 p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                    modoDistribuicao === 'seletivo' 
                      ? 'border-red-500 bg-red-50 text-red-700' 
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                  data-testid="dist-seletivo-btn"
                >
                  Selecionar Vendedores
                </button>
              </div>

              {modoDistribuicao === 'seletivo' && (
                <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-2 space-y-1">
                  {vendedores.length === 0 ? (
                    <p className="text-xs text-gray-400 text-center py-2">Nenhum vendedor ativo</p>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          if (vendedoresSelecionadosImport.length === vendedores.length) {
                            setVendedoresSelecionadosImport([]);
                          } else {
                            setVendedoresSelecionadosImport(vendedores.map(v => v.id));
                          }
                        }}
                        className="w-full text-left px-2 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50 rounded"
                      >
                        {vendedoresSelecionadosImport.length === vendedores.length ? 'Desmarcar Todos' : 'Selecionar Todos'}
                      </button>
                      {vendedores.map(v => (
                        <label
                          key={v.id}
                          className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm hover:bg-gray-50 ${
                            vendedoresSelecionadosImport.includes(v.id) ? 'bg-red-50' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={vendedoresSelecionadosImport.includes(v.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setVendedoresSelecionadosImport([...vendedoresSelecionadosImport, v.id]);
                              } else {
                                setVendedoresSelecionadosImport(vendedoresSelecionadosImport.filter(id => id !== v.id));
                              }
                            }}
                            className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                            data-testid={`vendor-checkbox-${v.id}`}
                          />
                          <span className="flex-1 truncate">{v.nome}</span>
                          <span className="text-xs text-gray-400 shrink-0">{v.perfil}</span>
                        </label>
                      ))}
                    </>
                  )}
                  {vendedoresSelecionadosImport.length > 0 && (
                    <p className="text-xs text-gray-500 text-center pt-1 border-t">
                      {vendedoresSelecionadosImport.length} selecionado(s)
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={handleImportarExcel}
              />
              {arquivoImportar ? (
                <div className="flex flex-col items-center gap-2">
                  <p className="text-sm font-medium text-gray-700">Arquivo: <strong>{arquivoImportar.name}</strong></p>
                  <Button variant="outline" size="sm" onClick={() => { setArquivoImportar(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}>
                    Trocar arquivo
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Upload className="h-8 w-8 text-gray-400" />
                  <Button onClick={() => fileInputRef.current?.click()} variant="outline">
                    Selecionar Arquivo Excel
                  </Button>
                  <p className="text-xs text-gray-500">Aceita .xlsx ou .xls</p>
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setModalImportarAberto(false);
              setArquivoImportar(null);
              if (fileInputRef.current) fileInputRef.current.value = '';
            }}>
              Cancelar
            </Button>
            <Button 
              onClick={confirmarImportacao} 
              disabled={importando || !arquivoImportar}
              className="bg-red-600 hover:bg-red-700"
            >
              {importando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
              Importar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Editar Lead Intermediário */}
      <Dialog open={leadAtivoEditando} onOpenChange={setLeadAtivoEditando}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5 text-red-600" />
              Editar Lead {propostaSelecionada?.tipo_mailing === 'BASE' ? '(Base)' : ''}
            </DialogTitle>
          </DialogHeader>
          {propostaSelecionada && (
            <div className="space-y-4">
              {/* Info header */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-semibold">{propostaSelecionada.nome_antigo || propostaSelecionada.nome || '-'}</p>
                <p className="text-sm text-gray-500">{propostaSelecionada.cidade} {propostaSelecionada.contrato_antigo ? `- ${propostaSelecionada.contrato_antigo}` : ''} {propostaSelecionada.contrato ? `- ${propostaSelecionada.contrato}` : ''}</p>
              </div>

              {/* Campos editáveis BASE */}
              {propostaSelecionada.tipo_mailing === 'BASE' && statusTratamento !== 'CANCELADO' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Nome</Label>
                    <Input value={propostaSelecionada.nome || ''} onChange={(e) => setPropostaSelecionada({...propostaSelecionada, nome: e.target.value})} className="mt-1" />
                  </div>
                  <div>
                    <Label>Telefone</Label>
                    <Input value={propostaSelecionada.telefone || ''} onChange={(e) => setPropostaSelecionada({...propostaSelecionada, telefone: e.target.value})} className="mt-1" />
                  </div>
                  <div>
                    <Label>CPF/CNPJ</Label>
                    <Input value={propostaSelecionada.cpf_cnpj || ''} onChange={(e) => setPropostaSelecionada({...propostaSelecionada, cpf_cnpj: e.target.value})} className="mt-1" />
                  </div>
                  <div>
                    <Label>Contrato</Label>
                    <Input value={propostaSelecionada.contrato || ''} onChange={(e) => setPropostaSelecionada({...propostaSelecionada, contrato: e.target.value})} className="mt-1" />
                  </div>
                  <div>
                    <Label>HP</Label>
                    <Input value={propostaSelecionada.hp || ''} onChange={(e) => setPropostaSelecionada({...propostaSelecionada, hp: e.target.value})} className="mt-1" />
                  </div>
                  <div>
                    <Label>Cidade</Label>
                    <Input value={propostaSelecionada.cidade || ''} onChange={(e) => setPropostaSelecionada({...propostaSelecionada, cidade: e.target.value})} className="mt-1" />
                  </div>
                  <div className="col-span-2">
                    <Label>Endereço</Label>
                    <Input value={propostaSelecionada.endereco || ''} onChange={(e) => setPropostaSelecionada({...propostaSelecionada, endereco: e.target.value})} className="mt-1" />
                  </div>
                </div>
              )}

              {/* Campos editáveis TT/PROSPECT */}
              {propostaSelecionada.tipo_mailing !== 'BASE' && statusTratamento !== 'CANCELADO' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Telefone</Label>
                    <Input value={propostaSelecionada.telefone || ''} onChange={(e) => setPropostaSelecionada({...propostaSelecionada, telefone: e.target.value})} className="mt-1" />
                  </div>
                  <div>
                    <Label>CPF/CNPJ</Label>
                    <Input value={propostaSelecionada.cpf_cnpj || ''} onChange={(e) => setPropostaSelecionada({...propostaSelecionada, cpf_cnpj: e.target.value})} className="mt-1" />
                  </div>
                </div>
              )}

              {/* Status */}
              <div>
                <Label>Status</Label>
                <Select value={statusTratamento} onValueChange={setStatusTratamento}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EM TRATATIVA">Em Tratativa</SelectItem>
                    <SelectItem value="AGUARDANDO CLIENTE">Aguardando Cliente</SelectItem>
                    <SelectItem value="FINALIZADO">Finalizado</SelectItem>
                    <SelectItem value="CANCELADO">Cancelado</SelectItem>
                    <SelectItem value="VENDA REALIZADA">Venda Realizada</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Observação */}
              <div>
                <Label>Observação</Label>
                <Textarea
                  value={propostaSelecionada.observacao_atendimento || ''}
                  onChange={(e) => setPropostaSelecionada({...propostaSelecionada, observacao_atendimento: e.target.value})}
                  rows={3}
                  placeholder="Observação do atendimento..."
                  className="mt-1"
                  disabled={statusTratamento === 'CANCELADO'}
                />
              </div>

              {statusTratamento === 'CANCELADO' && (
                <p className="text-xs text-red-500 italic">Lead cancelado - campos bloqueados para edição</p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setLeadAtivoEditando(false); setPropostaSelecionada(null); }}>
              Cancelar
            </Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={async () => {
              if (!statusTratamento) { toast.error('Selecione um status'); return; }
              try {
                // Send updated fields along with status
                const extraFields = {
                  observacao_atendimento: propostaSelecionada.observacao_atendimento || ''
                };
                // Include editable fields if not cancelled
                if (statusTratamento !== 'CANCELADO' && propostaSelecionada.tipo_mailing === 'BASE') {
                  extraFields.nome = propostaSelecionada.nome;
                  extraFields.telefone = propostaSelecionada.telefone;
                  extraFields.cpf_cnpj = propostaSelecionada.cpf_cnpj;
                  extraFields.contrato = propostaSelecionada.contrato;
                  extraFields.hp = propostaSelecionada.hp;
                  extraFields.cidade = propostaSelecionada.cidade;
                  extraFields.endereco = propostaSelecionada.endereco;
                } else if (statusTratamento !== 'CANCELADO') {
                  extraFields.telefone = propostaSelecionada.telefone;
                  extraFields.cpf_cnpj = propostaSelecionada.cpf_cnpj;
                }
                await mailingAPI.tratar(propostaSelecionada.id, statusTratamento, propostaSelecionada.observacao_atendimento || '', extraFields);
                toast.success('Lead atualizado!');
                setLeadAtivoEditando(false);
                setPropostaSelecionada(null);
                setStatusTratamento('');
                carregarDados();
              } catch (error) {
                toast.error(error.response?.data?.detail || 'Erro ao atualizar lead');
              }
            }} data-testid="btn-salvar-lead-edit">
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Visualizar Lead */}
      <Dialog open={leadVisualizando} onOpenChange={setLeadVisualizando}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-gray-600" />
              Detalhes do Lead
              {propostaSelecionada && (
                <Badge variant="outline" className="ml-2">{propostaSelecionada.tipo_mailing}</Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          {propostaSelecionada && (
            <div className="space-y-4 max-h-[65vh] overflow-y-auto">
              {/* Status e Info principal */}
              <div className="p-3 rounded-lg" style={{
                background: propostaSelecionada.status === 'VENDA REALIZADA' ? '#f0fdf4' :
                  propostaSelecionada.status === 'CANCELADO' ? '#fef2f2' :
                  propostaSelecionada.status === 'FINALIZADO' ? '#f0f9ff' : '#f9fafb',
                border: `1px solid ${
                  propostaSelecionada.status === 'VENDA REALIZADA' ? '#bbf7d0' :
                  propostaSelecionada.status === 'CANCELADO' ? '#fecaca' :
                  propostaSelecionada.status === 'FINALIZADO' ? '#bfdbfe' : '#e5e7eb'
                }`
              }}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-bold text-lg text-gray-900">{propostaSelecionada.nome || propostaSelecionada.nome_antigo || '-'}</span>
                  {getStatusBadge(propostaSelecionada.status)}
                </div>
                <p className="text-sm text-gray-500">{propostaSelecionada.tipo_mailing} {propostaSelecionada.numero_proposta ? `• #${propostaSelecionada.numero_proposta}` : ''}</p>
              </div>

              {/* Dados do Lead */}
              <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                <div>
                  <p className="text-xs font-medium text-gray-400 uppercase">Telefone</p>
                  <div className="flex items-center gap-2">
                    <p className="text-sm text-gray-900 font-medium">{propostaSelecionada.telefone || '-'}</p>
                    {propostaSelecionada.telefone && (
                      <div className="flex items-center gap-1">
                        <a href={`tel:${telLink(propostaSelecionada.telefone)}`} title="Ligar" className="p-1 rounded hover:bg-green-100 text-green-600"><Phone className="w-4 h-4" /></a>
                        <a href={`https://wa.me/55${telLink(propostaSelecionada.telefone)}`} target="_blank" rel="noreferrer" title="WhatsApp" className="p-1 rounded hover:bg-emerald-100 text-emerald-600"><MessageCircle className="w-4 h-4" /></a>
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-400 uppercase">CPF/CNPJ</p>
                  <p className="text-sm text-gray-900 font-medium">{limparDoc(propostaSelecionada.cpf_cnpj)}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-400 uppercase">Cidade</p>
                  <p className="text-sm text-gray-900">{propostaSelecionada.cidade || '-'} {propostaSelecionada.estado ? `/ ${propostaSelecionada.estado}` : ''}</p>
                </div>
                {(propostaSelecionada.contrato_antigo || propostaSelecionada.contrato) && (
                  <div>
                    <p className="text-xs font-medium text-gray-400 uppercase">Contrato</p>
                    <p className="text-sm text-gray-900">{propostaSelecionada.contrato_antigo || propostaSelecionada.contrato || '-'}</p>
                  </div>
                )}
                {propostaSelecionada.hp && (
                  <div>
                    <p className="text-xs font-medium text-gray-400 uppercase">HP</p>
                    <p className="text-sm text-gray-900">{propostaSelecionada.hp}</p>
                  </div>
                )}
                {propostaSelecionada.endereco && (
                  <div className="col-span-2">
                    <p className="text-xs font-medium text-gray-400 uppercase">Endereço</p>
                    <p className="text-sm text-gray-900">
                      {propostaSelecionada.endereco}
                      {propostaSelecionada.numero_end ? `, ${propostaSelecionada.numero_end}` : ''}
                      {propostaSelecionada.complemento ? ` - ${propostaSelecionada.complemento}` : ''}
                    </p>
                  </div>
                )}
                {propostaSelecionada.bairro && (
                  <div>
                    <p className="text-xs font-medium text-gray-400 uppercase">Bairro</p>
                    <p className="text-sm text-gray-900">{propostaSelecionada.bairro}</p>
                  </div>
                )}
                {propostaSelecionada.cep && (
                  <div>
                    <p className="text-xs font-medium text-gray-400 uppercase">CEP</p>
                    <p className="text-sm text-gray-900">{propostaSelecionada.cep}</p>
                  </div>
                )}
                {propostaSelecionada.email_lead && (
                  <div>
                    <p className="text-xs font-medium text-gray-400 uppercase">E-mail</p>
                    <p className="text-sm text-gray-900">{propostaSelecionada.email_lead}</p>
                  </div>
                )}
                {propostaSelecionada.nascimento && (
                  <div>
                    <p className="text-xs font-medium text-gray-400 uppercase">Nascimento</p>
                    <p className="text-sm text-gray-900">{propostaSelecionada.nascimento}</p>
                  </div>
                )}
                {propostaSelecionada.nome_mae && (
                  <div>
                    <p className="text-xs font-medium text-gray-400 uppercase">Nome da Mãe</p>
                    <p className="text-sm text-gray-900">{propostaSelecionada.nome_mae}</p>
                  </div>
                )}
              </div>

              {/* Separador - Info de Tratamento */}
              {(propostaSelecionada.vendedor_nome || propostaSelecionada.vendedor_tratou) && (
                <>
                  <div className="border-t pt-3">
                    <p className="text-xs font-medium text-gray-400 uppercase mb-2">Informações de Tratamento</p>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                      {propostaSelecionada.vendedor_nome && (
                        <div>
                          <p className="text-xs font-medium text-gray-400 uppercase">Vendedor Atribuído</p>
                          <p className="text-sm text-gray-900">{propostaSelecionada.vendedor_nome}</p>
                        </div>
                      )}
                      {propostaSelecionada.vendedor_tratou && (
                        <div>
                          <p className="text-xs font-medium text-gray-400 uppercase">Tratado por</p>
                          <p className="text-sm text-gray-900">{propostaSelecionada.vendedor_tratou}</p>
                        </div>
                      )}
                      {propostaSelecionada.data_tratamento && (
                        <div>
                          <p className="text-xs font-medium text-gray-400 uppercase">Data Tratamento</p>
                          <p className="text-sm text-gray-900">{propostaSelecionada.data_tratamento} {propostaSelecionada.hora_tratamento ? `às ${propostaSelecionada.hora_tratamento}` : ''}</p>
                        </div>
                      )}
                      {propostaSelecionada.data_atribuicao && (
                        <div>
                          <p className="text-xs font-medium text-gray-400 uppercase">Data Atribuição</p>
                          <p className="text-sm text-gray-900">{propostaSelecionada.data_atribuicao}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Observação */}
              {(propostaSelecionada.observacao_atendimento || propostaSelecionada.observacao) && (
                <div className="border-t pt-3">
                  <p className="text-xs font-medium text-gray-400 uppercase mb-1">Observação</p>
                  <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg whitespace-pre-wrap">
                    {propostaSelecionada.observacao_atendimento || propostaSelecionada.observacao || '-'}
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setLeadVisualizando(false); setPropostaSelecionada(null); }} data-testid="btn-fechar-detalhes-lead">
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Mailing;
